import { TableProps, TableVirtuoso } from 'react-virtuoso';
import {
  DataTableSortableProps,
  DataTableRow,
  DataTableSortStateProps,
  DataTableVariant,
} from './TableTypes';
import { TableHeadRow } from './TableHeadRow';
import { TableLoader } from './TableLoader';
import { StyledTableBody, StyledTableHead } from './StyledTableComponents';
import { Table, TableRowProps, TableRow as MuiTableRow, Stack } from '@mui/material';
import { forwardRef, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import TableRow from './TableRow';
import { SortOrder } from '../types';
import { ReachedLastRow } from './ReachedLastRow';
import { ACTION_BUTTONS_KEY } from './utils';
import { colors } from '@sweep-io/sweep-design';

/**
 * Supports out of the box support of virtual scrolling for smooth big amounts of data rendering
 */
export function VirtualScrollDataTable<TRow extends DataTableRow>({
  rows,
  columns: allColumns,
  onRowClick,
  containerRef,
  onSortChange,
  defaultSortState,
  tableEmptyStateJsx,
  sxRowFunction,
  isLoading,
  variant = DataTableVariant.medium,
  reachedLastRow,
  hideChevronButton,
  noTableBorder,
  tableId,
}: Pick<
  DataTableSortableProps<TRow>,
  | 'onRowClick'
  | 'columns'
  | 'rows'
  | 'containerRef'
  | 'onSortChange'
  | 'defaultSortState'
  | 'tableEmptyStateJsx'
  | 'isLoading'
  | 'variant'
  | 'sxRowFunction'
  | 'reachedLastRow'
  | 'hideChevronButton'
  | 'tableId'
> & { noTableBorder?: boolean }) {
  const [sortState, setSortState] = useState<DataTableSortStateProps | undefined>(defaultSortState);
  const noActionColumns = useMemo(
    () => allColumns.filter((column) => !column.hidden && column.field !== ACTION_BUTTONS_KEY),
    [allColumns],
  );
  const _tableRef: RefObject<any> = useRef(null);
  const tableRef = containerRef ?? _tableRef;

  const _sort = useCallback(
    (columnName: string) => {
      const newState = {
        sortBy: columnName,
        sortOrder: sortState?.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
      };

      setSortState(newState);
      onSortChange && onSortChange(newState);
    },
    [onSortChange, sortState?.sortOrder],
  );

  const renderHeader = useCallback(() => {
    return (
      <TableHeadRow
        tableId={tableId}
        columns={noActionColumns}
        stickyHeader
        variant={variant}
        sort={_sort}
        sortState={sortState}
      />
    );
  }, [noActionColumns, tableId, _sort, sortState, variant]);

  const components = useMemo(() => {
    return {
      Table: forwardRef<any, TableProps>(({ children, style }, ref) => {
        return (
          <Table
            ref={ref}
            style={
              {
                '--virtuosoPaddingTop': (style?.paddingTop ?? 0) + 'px',
                '--virtuosoPaddingBottom': (style?.paddingBottom ?? 0) + 'px',
              } as any
            }
          >
            {children}
          </Table>
        );
      }),
      TableHead: forwardRef<any, TableProps>(({ children }, ref) => {
        return <StyledTableHead ref={ref}>{children}</StyledTableHead>;
      }),
      TableBody: forwardRef<any, TableProps>((props, ref) => {
        return (
          <StyledTableBody ref={ref} style={{ position: rows.length ? 'relative' : 'initial' }}>
            {!!rows.length ? (
              props.children
            ) : (
              <MuiTableRow
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  '&:hover': { backgroundColor: 'transparent !important' },
                }}
              >
                {tableEmptyStateJsx}
              </MuiTableRow>
            )}
          </StyledTableBody>
        );
      }),
      TableRow: forwardRef<any, TableRowProps>((props: any, ref) => {
        const row = props.item;
        const idx = props['data-index'];

        return (
          <>
            <TableRow
              hideChevronButton={hideChevronButton}
              data-index={idx}
              ref={ref}
              row={row}
              columns={noActionColumns}
              sxRowFunction={sxRowFunction}
              tableRowProps={{ id: `table_row_${row.id}` }}
              onClick={
                onRowClick
                  ? (e, column) => {
                      onRowClick && onRowClick(e, row, column);
                    }
                  : undefined
              }
              variant={variant}
              {...props}
            />

            {reachedLastRow && idx === reachedLastRow?.rowsMaxCount - 1 && (
              <ReachedLastRow
                columnsCount={noActionColumns.length}
                reachedLastRowTxt={reachedLastRow.txt}
                scrollToTop={() => {
                  (containerRef?.current as any)?.scrollToIndex({
                    index: 0,
                    top: 0,
                    behavior: 'smooth',
                  });
                }}
              />
            )}
          </>
        );
      }),
    };
  }, [
    tableEmptyStateJsx,
    noActionColumns,
    onRowClick,
    rows.length,
    sxRowFunction,
    variant,
    containerRef,
    reachedLastRow,
    hideChevronButton,
  ]);

  return (
    <Stack
      height="100%"
      position="relative"
      sx={{
        border: noTableBorder ? 'none' : `1px solid ${colors.grey[120]}`,
        borderRadius: noTableBorder ? 'none' : '8px',
        overflow: 'hidden',
      }}
    >
      {isLoading && <TableLoader />}

      <TableVirtuoso
        ref={tableRef}
        totalCount={rows.length}
        data={rows}
        components={components}
        fixedHeaderContent={renderHeader}
      />
    </Stack>
  );
}
